// features/notifications/meta.ts — static tables for the notifications UI.

import type { IconName } from '@/icons';
import type { SocialNotificationType, CouncilNotificationKind } from '@/core/models/notification';
import type { TypeFilter, ScopeId } from './filterNotifications';

export interface TypeTab {
  id: TypeFilter;
  label: string;
}

export const TYPE_TABS: TypeTab[] = [
  { id: 'all',     label: 'All' },
  { id: 'mention', label: 'Mentions' },
  { id: 'reply',   label: 'Replies' },
  { id: 'cherish', label: 'Cherishes' },
  { id: 'spread',  label: 'Spreads' },
  { id: 'quote',   label: 'Quotes' },
  { id: 'follow',  label: 'Follows' },
];

export interface ScopeOption {
  id: ScopeId;
  label: string;
}

export const SCOPES: ScopeOption[] = [
  { id: 'all',       label: 'All Notifications' },
  { id: 'unread',    label: 'Unread' },
  { id: 'following', label: 'From People You Follow' },
  { id: 'mentions',  label: 'Mentions Only' },
];

/**
 * What an empty list says, keyed by the active type tab.
 *
 * The page used to say "No notifications match these filters." whenever the list
 * came back empty — including for a brand-new account on the default All/All view,
 * where no filter has been applied at all. To a member who has just joined, that
 * reads as "something is hiding your notifications", which is the opposite of true.
 * A list may only blame a filter when a filter is actually narrowing it.
 *
 * Ported from mobile's NotificationsTab.tsx, which already did this correctly.
 */
export const EMPTY_BY_TYPE: Record<TypeFilter, string> = {
  all:     'No notifications yet.',
  mention: 'No mentions yet.',
  reply:   'No replies yet.',
  like:    'No cherishes yet.',
  cherish: 'No cherishes yet.',
  spread:  'No spreads yet.',
  quote:   'No quotes yet.',
  follow:  'No follows yet.',
  follow_request:  'No follow requests yet.',
  follow_accepted: 'No accepted follow requests yet.',
  post:    'No posts yet.',
  dm:      'No messages yet.',
  council: 'No council notifications yet.',
  feedback: 'No feedback updates yet.',
};

/** The message for an empty list under the current type + scope selection. */
export function emptyNotificationsLabel(type: TypeFilter, scope: ScopeId): string {
  // Scope genuinely narrows the list, so when one is set, saying so is accurate.
  if (scope !== 'all') return 'No notifications match these filters.';
  return EMPTY_BY_TYPE[type];
}

export interface TypeMetaEntry {
  icon: IconName;
  verb: string;
}

// Teal-HUD: the icon CHIP is always teal (the GLYPH differentiates the type),
// so there is no per-type tone color here — see NotificationCard's IconChip.
export const TYPE_META: Record<SocialNotificationType, TypeMetaEntry> = {
  // Likes were merged into Cherish; an older "like" row reads as the Cherish it now is.
  like:    { icon: 'cherish',   verb: 'cherished your post' },
  cherish: { icon: 'cherish',   verb: 'cherished your post' },
  reply:   { icon: 'comment',   verb: 'replied to your post' },
  mention: { icon: 'at-sign',   verb: 'mentioned you' },
  follow:  { icon: 'user-plus', verb: 'started following you' },
  follow_request:  { icon: 'user-plus', verb: 'requested to follow you' },
  follow_accepted: { icon: 'user-check', verb: 'accepted your follow request' },
  spread:  { icon: 'spread-it', verb: 'spread your post' },
  quote:   { icon: 'quote',     verb: 'quoted your post' },
  post:    { icon: 'newspaper', verb: 'posted' },
  dm:      { icon: 'mail',      verb: 'sent you a message' },
  feedback: { icon: 'feedback', verb: 'updated feedback you follow' },
};

// Recurrence guard: the backend can start emitting a notification `type` before
// the clients know about it (see the sync tests against
// backend/apps/social/models.py's Notification.type choices). Rather than
// throwing on a missing key, unknown types render this generic row.
export const FALLBACK_TYPE_META: TypeMetaEntry = {
  icon: 'bell',
  verb: 'sent you a notification',
};

/** Looks up TYPE_META for a social notification type, never throwing on an unknown value. */
export function metaFor(type: string): TypeMetaEntry {
  return (TYPE_META as Record<string, TypeMetaEntry>)[type] ?? FALLBACK_TYPE_META;
}

export const COUNCIL_KIND_LABEL: Record<CouncilNotificationKind, string> = {
  'under-review': 'Under review',
  'cleared':      'Cleared',
  'appeal':       'Appeal update',
  'login-alert':  'New sign-in',
};

export const FALLBACK_COUNCIL_LABEL = 'Council notification';

/** Looks up COUNCIL_KIND_LABEL for a council kind, never rendering "undefined". */
export function councilLabelFor(kind: string | undefined): string {
  if (!kind) return FALLBACK_COUNCIL_LABEL;
  return (COUNCIL_KIND_LABEL as Record<string, string>)[kind] ?? FALLBACK_COUNCIL_LABEL;
}
